/*
 * Prezzi scontati Regios (DOPP) su tutte le schede prodotto.
 *
 * Perche': Regios disegna il prezzo scontato solo nelle griglie coperte da un
 * suo blocco app, da configurare a mano sezione per sezione. Le altre schede
 * (articoli del blog, visti di recente, consigliati nel carrello, caroselli
 * senza blocco, "carica altri", tab della home) restavano a prezzo pieno.
 *
 * Come: snippets/price.liquid mette su ogni prezzo di scheda l'attributo
 * data-dopp-product con i dati per il calcolo (collezioni, tag, marca,
 * varianti...). Qui si chiede a Regios il prezzo con
 * api.calculateDiscountedPrices e lo si scrive nel markup prezzi del tema:
 * f-price--on-sale, prezzo scontato (con "Da" se la scheda lo aveva), prezzo
 * pieno barrato, prezzo unitario e badge saldi sull'immagine secondo le
 * impostazioni "Schede prodotto". Stile standard e layout Promo valgono da
 * soli, perche' le classi sono quelle del tema.
 *
 * Un solo disegnatore per prezzo: con l'interceptor di Regios si spegne il
 * suo disegno delle schede (tipi griglia) per i prodotti che una nostra
 * scheda ha gia' calcolato. Se il nostro calcolo non va a buon fine (errore,
 * timeout, prezzo incoerente, sezione esclusa) il prodotto torna a Regios,
 * come oggi. PRODUCT_PAGE resta sempre a Regios, e i prezzi della pagina
 * prodotto non hanno data-dopp-product (price.liquid con use_variant).
 *
 * Sicurezza:
 * - senza API Regios, o senza interceptor, non si tocca nulla: meglio il
 *   comportamento di oggi che un doppio sconto a video;
 * - si calcola sempre dai dati Liquid, mai dal testo nel DOM: le slide
 *   duplicate di Swiper e le schede reinserite non si scontano due volte;
 * - se il prezzo pieno di Regios non torna con quello della scheda (valuta,
 *   dati vecchi) la scheda non si tocca e il disegno resta a Regios;
 * - schede inserite dopo il caricamento (filtri, carica altri, carrello,
 *   consigliati, tab da <template>, ricerca rapida): un solo MutationObserver,
 *   perche' il tema non emette eventi comuni. Le nostre scritture nascono
 *   dentro un .f-price gia' elaborato e non generano nuovi candidati.
 *
 * Console: ?dopp-cards=debug per i log, ?dopp-cards=off per spegnere tutto;
 * DoppCards.stats() e DoppCards.rescan().
 */
(() => {
  'use strict';

  // Una sola istanza, anche se il file venisse incluso due volte.
  if (window.DoppCards) return;

  const SELECTOR = '.f-price[data-dopp-product]';
  const PRICE_TOKEN = '__DOPP_PRICE__';
  const AMOUNT_TOKEN = '__DOPP_AMOUNT__';
  // Tipi con cui Regios disegna griglie e caroselli. PRODUCT_PAGE non c'e' apposta.
  const CARD_TYPES = ['COLLECTION_PAGE', 'SEARCH_PAGE', 'PRODUCT_RECOMMENDATIONS', 'FEATURED_PRODUCTS'];
  // Schede senza area badge sull'immagine (pcard-badges non ci viene reso):
  // orizzontale (carrello, complementari), lista, immagine con prodotto.
  const NO_BADGE_CARDS = '.product-card--horizontal, .product-card--simple, .product-card--list';
  // Parti del tema che la pulizia dei nodi Regios non rimuove mai.
  const THEME_PARTS =
    '.f-price, .f-price-item, .f-price__regular, .f-price__sale, .f-price__unit-wrapper, ' +
    '.product-card__image-wrapper, .product-card__info, .product-card__badge, .product-card__title, img';

  const config = Object.assign(
    {
      fromPrice: '',
      regularLabel: '',
      showBadge: true,
      badgeType: 'show_text',
      badgeText: '',
      saveTemplate: '',
      unitMoneyFormat: '',
      showDescription: false,
      concurrency: 4,
      timeoutMs: 8000
    },
    window.DoppCardsConfig || {}
  );

  let mode = '';
  try {
    mode = new URLSearchParams(window.location.search).get('dopp-cards') || '';
  } catch (error) {
    mode = '';
  }
  const debug = mode === 'debug';
  const log = (...args) => {
    if (debug) console.info('[dopp-cards]', ...args);
  };
  const warned = new Set();
  const warnOnce = (key, ...args) => {
    if (warned.has(key)) return;
    warned.add(key);
    console.warn('[dopp-cards]', ...args);
  };

  if (mode === 'off') return;

  let api = null;
  let started = false;
  let calls = 0;
  const seen = new WeakSet();
  const jobs = new WeakMap();
  const cache = new Map();
  // Risultati gia' arrivati: le schede reinserite si disegnano subito.
  const resolved = new Map();
  const injectedCss = new Set();

  /* --- Coda: al massimo `concurrency` calcoli contemporanei ---------------- */

  let active = 0;
  const queue = [];
  const pump = () => {
    while (active < config.concurrency && queue.length) {
      const job = queue.shift();
      active += 1;
      job().then(() => {
        active -= 1;
        pump();
      });
    }
  };
  const limit = (task) =>
    new Promise((resolve, reject) => {
      queue.push(() => Promise.resolve().then(task).then(resolve, reject));
      pump();
    });

  const withTimeout = (promise, ms) =>
    new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('timeout')), ms);
      promise.then(
        (value) => {
          clearTimeout(timer);
          resolve(value);
        },
        (error) => {
          clearTimeout(timer);
          reject(error);
        }
      );
    });

  /* --- Dati della scheda --------------------------------------------------- */

  const readPayload = (el) => {
    try {
      const data = JSON.parse(el.getAttribute('data-dopp-product'));
      if (
        !data ||
        !Number.isFinite(data.productId) ||
        !Number.isFinite(data.variantId) ||
        !Number.isFinite(data.regularPriceInCents)
      ) {
        return null;
      }
      return data;
    } catch (error) {
      return null;
    }
  };

  const keyOf = (data) =>
    [data.productId, data.variantId, data.regularPriceInCents, data.compareAtPriceInCents || 0].join(':');

  // Solo i campi dell'API: from, onSale, available e unitPriceInCents servono
  // al disegno. Copie, cosi' l'API non puo' alterare i dati letti.
  const toArgs = (data) => {
    const args = {
      productId: data.productId,
      variantId: data.variantId,
      regularPriceInCents: data.regularPriceInCents,
      handle: typeof data.handle === 'string' ? data.handle : '',
      url: typeof data.url === 'string' ? data.url : '',
      vendor: typeof data.vendor === 'string' ? data.vendor : '',
      tags: Array.isArray(data.tags) ? data.tags.slice() : [],
      collectionIds: Array.isArray(data.collectionIds) ? data.collectionIds.slice() : [],
      quantity: 1
    };
    if (Number.isFinite(data.compareAtPriceInCents)) args.compareAtPriceInCents = data.compareAtPriceInCents;
    if (Array.isArray(data.variants) && data.variants.length) {
      args.variants = data.variants.map((variant) => Object.assign({}, variant));
    }
    return args;
  };

  // Stesso prodotto/variante/prezzo = un solo calcolo per pagina (mega menu,
  // menu mobile e caroselli ripetono spesso gli stessi prodotti).
  const calculate = (data) => {
    const key = keyOf(data);
    let pending = cache.get(key);
    if (!pending) {
      pending = limit(() => {
        calls += 1;
        return withTimeout(Promise.resolve(api.calculateDiscountedPrices(toArgs(data))), config.timeoutMs);
      });
      cache.set(key, pending);
      pending.then(
        (result) => resolved.set(key, result),
        // Un errore non resta in cache: la prossima scheda uguale riprova.
        () => cache.delete(key)
      );
    }
    return pending;
  };

  /* --- Esito del calcolo --------------------------------------------------- */

  const toCents = (price) => {
    const amount = price && price.amount;
    if (amount === null || amount === undefined || amount === '') return NaN;
    const number = Number(amount);
    return Number.isFinite(number) ? Math.round(number * 100) : NaN;
  };

  // 'sale' | 'none' | 'mismatch' | 'error'
  const evaluate = (data, result) => {
    if (!result || !result.salePrice || !result.regularPrice) return 'error';
    const sale = toCents(result.salePrice);
    const regular = toCents(result.regularPrice);
    if (!(sale >= 0) || !(regular > 0)) return 'error';
    // Sconto dell'app solo se il prezzo scende sotto quello che la scheda gia'
    // mostra: un barrato da compare-at non e' uno sconto Regios.
    if (!(sale < data.regularPriceInCents) || !(sale < regular)) return 'none';
    // Coerenza: il prezzo pieno di Regios deve essere il prezzo o il barrato
    // della scheda. Se non torna (valuta diversa, dati vecchi) non si tocca nulla.
    const known = [data.regularPriceInCents, data.compareAtPriceInCents].filter(Number.isFinite);
    if (!known.some((cents) => Math.abs(cents - regular) <= 1)) return 'mismatch';
    return 'sale';
  };

  /* --- Disegno nel markup di snippets/price.liquid ------------------------- */

  const HTML_ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  const escapeHtml = (value) => String(value).replace(/[&<>"']/g, (char) => HTML_ESCAPES[char]);

  // Stessa formattazione del tema (FoxTheme.Currency, formato di
  // snippets/js-variables.liquid): il prezzo scontato si legge come gli altri.
  const themeMoney = (cents, format) => {
    const theme = window.FoxTheme;
    const moneyFormat = format || (theme && theme.settings && theme.settings.moneyFormat);
    if (!moneyFormat || !theme || !theme.Currency || typeof theme.Currency.formatMoney !== 'function') return '';
    try {
      return theme.Currency.formatMoney(cents, moneyFormat) || '';
    } catch (error) {
      return '';
    }
  };
  const moneyHtml = (cents, fallback) => themeMoney(cents) || (fallback ? escapeHtml(fallback) : '');

  const injectCss = (css) => {
    if (!css || injectedCss.has(css)) return;
    injectedCss.add(css);
    const style = document.createElement('style');
    style.setAttribute('data-dopp-cards', '');
    style.textContent = css;
    document.head.appendChild(style);
  };

  // Se Regios ha disegnato prima che l'interceptor fosse attivo: si tolgono i
  // nodi creati da lui (solo classi regios-dopp*); ai nodi del tema che ha
  // soltanto marcato (form, marca, badge...) si tolgono le classi dell'app,
  // senza rimuoverli. Lo stato finale e' lo stesso chiunque sia arrivato primo.
  const isRegiosNode = (node) =>
    node.classList.length > 0 && Array.from(node.classList).every((name) => name.indexOf('regios-dopp') === 0);

  const clearRegios = (el) => {
    const scope = el.closest('.product-card__wrapper') || el;
    let touched = false;
    scope.querySelectorAll('[class*="regios-dopp"]').forEach((node) => {
      if (!scope.contains(node)) return; // gia' rimosso insieme a un antenato
      touched = true;
      if (isRegiosNode(node) && !node.contains(el) && !node.matches(THEME_PARTS) && !node.querySelector(THEME_PARTS)) {
        node.remove();
      } else {
        Array.from(node.classList).forEach((name) => {
          if (name.indexOf('regios-dopp') === 0) node.classList.remove(name);
        });
      }
    });
    // Il tema non nasconde mai il prezzo con uno stile inline: se c'e', l'ha
    // messo Regios accanto al suo prezzo, e va tolto.
    if (touched) {
      [el, ...el.querySelectorAll('.f-price__regular, .f-price__sale, .f-price-item')].forEach((node) => {
        if (node.style.display === 'none') node.style.removeProperty('display');
      });
    }
  };

  // Testo del badge come snippets/pcard-badges.liquid, secondo
  // Impostazioni tema > Schede prodotto > tipo di badge saldi.
  const badgeHtml = (saleCents, strikeCents) => {
    if (config.badgeType === 'show_percentage') {
      const percent = Math.floor(((strikeCents - saleCents) * 100) / strikeCents);
      return percent > 0 ? `-${percent}%` : '';
    }
    if (config.badgeType === 'show_amount') {
      const amount = themeMoney(strikeCents - saleCents, config.unitMoneyFormat);
      if (!amount || config.saveTemplate.indexOf(AMOUNT_TOKEN) === -1) return '';
      return config.saveTemplate.replace(AMOUNT_TOKEN, () => amount);
    }
    return config.badgeText ? escapeHtml(config.badgeText) : '';
  };

  // Badge nella stessa area e con le stesse classi di pcard-badges.liquid
  // (nel layout Promo diventa la pastiglia in alto a destra). Se il tema ha
  // gia' un badge saldi (compare-at) si aggiorna quello, non se ne aggiunge
  // un secondo.
  const renderBadge = (el, saleCents, strikeCents) => {
    const wrapper = el.closest('.product-card__wrapper');
    const card = wrapper && wrapper.closest('.product-card');
    if (!card || card.matches(NO_BADGE_CARDS)) return;
    const imageWrapper = wrapper.querySelector(':scope > .product-card__image-wrapper');
    if (!imageWrapper) return;
    const html = badgeHtml(saleCents, strikeCents);
    if (!html) return;

    let box = imageWrapper.querySelector(':scope > .product-card__badge');
    if (!box) {
      box = document.createElement('div');
      box.className = 'product-card__badge';
      imageWrapper.appendChild(box);
    }
    let badge = box.querySelector(':scope > .f-badge--sale');
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'f-badge f-badge--sale';
      badge.setAttribute('aria-hidden', 'true');
      badge.setAttribute('data-dopp-badge', '');
      // stesso ordine di pcard-badges.liquid: saldi prima dei badge da tag
      // (il badge esaurito non c'e': sulle schede esaurite il saldi non va)
      box.insertBefore(badge, box.firstChild);
    }
    if (badge.innerHTML !== html) badge.innerHTML = html;
  };

  const render = (el, data, result) => {
    const saleBox = el.querySelector(':scope > .f-price__sale');
    const saleItem = saleBox && saleBox.querySelector('.f-price-item--sale');
    if (!saleItem) return false;

    const saleCents = toCents(result.salePrice);
    const regularCents = toCents(result.regularPrice);
    const saleMoney = moneyHtml(saleCents, result.salePrice.formatted);
    const regularMoney = moneyHtml(regularCents, result.regularPrice.formatted);
    if (!saleMoney || !regularMoney) return false;

    clearRegios(el);

    // Prezzo scontato, con "Da" se la scheda mostrava un prezzo "a partire da".
    saleItem.innerHTML =
      data.from && config.fromPrice.indexOf(PRICE_TOKEN) !== -1
        ? config.fromPrice.replace(PRICE_TOKEN, () => saleMoney)
        : saleMoney;

    // Barrato: se il tema barrava gia' il compare-at lo si lascia (resta il
    // riferimento del negozio); altrimenti si barra il prezzo pieno.
    let strikeCents = regularCents;
    let regularItem = saleBox.querySelector('.f-price-item--regular');
    if (data.onSale && regularItem) {
      strikeCents = Math.max(regularCents, Number(data.compareAtPriceInCents) || 0);
    } else {
      if (!regularItem) {
        // price.liquid non lo stampa quando il prezzo non varia ma il compare-at si'.
        const nodes = [];
        if (config.regularLabel) {
          const label = document.createElement('span');
          label.className = 'visually-hidden visually-hidden--inline';
          label.textContent = config.regularLabel;
          nodes.push(label);
        }
        regularItem = document.createElement('span');
        regularItem.className = 'f-price-item f-price-item--regular';
        nodes.push(regularItem);
        saleItem.after(...nodes);
      }
      regularItem.innerHTML = `<s>${regularMoney}</s>`;
    }

    // Prezzo per unita' di misura: stesso rapporto dello sconto. Liquid lo passa
    // solo se riguarda la stessa variante del prezzo.
    if (Number.isFinite(data.unitPriceInCents) && data.unitPriceInCents > 0 && data.regularPriceInCents > 0) {
      const unit = el.querySelector('[data-unit-price]');
      const text = themeMoney(
        Math.round((data.unitPriceInCents * saleCents) / data.regularPriceInCents),
        config.unitMoneyFormat
      );
      if (unit && text) unit.innerHTML = text;
    }

    const oldDescription = el.querySelector(':scope > .dopp-card-description');
    if (oldDescription) oldDescription.remove();
    if (config.showDescription && result.description && result.description.html) {
      const description = document.createElement('div');
      description.className = 'dopp-card-description';
      description.innerHTML = result.description.html;
      el.appendChild(description);
      injectCss(result.description.css);
    }

    el.classList.add('f-price--on-sale');
    // Come pcard-badges.liquid: niente badge saldi sulle schede esaurite.
    if (config.showBadge && data.available !== false) renderBadge(el, saleCents, strikeCents);
    return true;
  };

  const mark = (el, state, key) => {
    el.setAttribute('data-dopp-state', state);
    if (key) el.setAttribute('data-dopp-key', key);
  };

  /* --- Elaborazione di un prezzo ------------------------------------------- */

  const process = (el) => {
    if (seen.has(el)) return null;
    seen.add(el);

    // Rete di sicurezza oltre al filtro Liquid: prezzi da pagina prodotto
    // (anche se qualcuno forzasse dopp: true) e contenitori esclusi a mano.
    if (el.classList.contains('f-price--large') || el.closest('sticky-atc-bar, sticky-atc-bar-minimalist, [data-dopp-skip]')) {
      mark(el, 'skipped');
      return null;
    }

    const data = readPayload(el);
    if (!data) {
      mark(el, 'error');
      return null;
    }
    const key = keyOf(data);
    // Copia di una scheda gia' conclusa (slide duplicata di Swiper, HTML rimesso
    // dal tema): il markup copiato e' gia' quello finale.
    if (el.getAttribute('data-dopp-key') === key && el.hasAttribute('data-dopp-state')) return null;

    const finish = (result) => {
      try {
        let state = evaluate(data, result);
        if (state === 'sale' && !render(el, data, result)) state = 'error';
        if (state === 'mismatch') {
          warnOnce('mismatch', 'prezzo Regios diverso da quello della scheda: il disegno resta a Regios', data, result);
        }
        // Gli errori non lasciano la chiave: una copia della scheda riprova.
        mark(el, state, state === 'error' ? null : key);
        log(state, data.productId, data.variantId, result);
      } catch (error) {
        mark(el, 'error');
        warnOnce('render', 'errore nel disegnare il prezzo scontato', error);
      }
    };

    // Risultato gia' noto (stesso prodotto altrove in pagina, scheda
    // reinserita da filtri, carrello o tab): si disegna subito, nello stesso
    // frame in cui il tema inserisce la scheda, senza passare dal prezzo pieno.
    if (resolved.has(key)) {
      finish(resolved.get(key));
      return null;
    }

    const job = calculate(data).then(finish, (error) => {
      mark(el, 'error');
      log('calcolo non riuscito', data.productId, error);
    });
    jobs.set(el, job);
    return job;
  };

  const isNear = (el) => {
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.bottom > 0 && rect.top < (window.innerHeight || 0) + 200;
  };

  const scan = (node) => {
    if (!node || node.nodeType !== 1) return [];
    const found = node.matches(SELECTOR) ? [node] : Array.from(node.querySelectorAll(SELECTOR));
    // Prima i prezzi visibili: sono quelli che il cliente sta guardando.
    // Tutte le misure prima di qualsiasi scrittura, per un solo calcolo di layout.
    const near = [];
    const far = [];
    found.forEach((el) => {
      if (seen.has(el)) return;
      (isNear(el) ? near : far).push(el);
    });
    const jobs = [];
    near.concat(far).forEach((el) => {
      const job = process(el);
      if (job) jobs.push(job);
    });
    return jobs;
  };

  /* --- Schede inserite dopo il caricamento --------------------------------- */

  const pendingNodes = new Set();
  let flushQueued = false;
  const flush = () => {
    flushQueued = false;
    const nodes = Array.from(pendingNodes);
    pendingNodes.clear();
    nodes.forEach((node) => {
      if (node.isConnected) scan(node);
    });
  };
  const observer = new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        // Cio' che scriviamo noi (prezzo, <s>, descrizione) nasce dentro un
        // .f-price: ignorato senza costo, quindi nessun ciclo.
        const parent = node.parentElement;
        if (parent && parent.closest('.f-price')) return;
        pendingNodes.add(node);
      });
    });
    // In microtask, prima del prossimo disegno: le schede gia' calcolate
    // compaiono direttamente scontate.
    if (pendingNodes.size && !flushQueued) {
      flushQueued = true;
      queueMicrotask(flush);
    }
  });

  /* --- Interceptor: Regios non disegna le schede che sono nostre ----------- */

  // Regios si spegne solo dove una nostra scheda ha davvero una risposta
  // ('sale' disegnata, 'none' senza sconto). Con 'mismatch', 'error' o
  // 'skipped' la scheda resta a Regios, come oggi.
  const ownsProduct = async (productId) => {
    const id = String(productId == null ? '' : productId).replace(/\D/g, '');
    if (id === '') return false;
    const els = Array.from(document.querySelectorAll(`.f-price[data-dopp-pid="${id}"]`)).filter(
      (el) => !el.closest('[data-dopp-skip]')
    );
    if (!els.length) return false;
    await Promise.all(els.map((el) => jobs.get(el) || process(el)));
    return els.some((el) => {
      const state = el.getAttribute('data-dopp-state');
      return state === 'sale' || state === 'none';
    });
  };

  const installInterceptor = async () => {
    const previous = await api.interceptors.getPriceRenderingInterceptor();
    await api.interceptors.setPriceRenderingInterceptor(async (event) => {
      try {
        if (
          event &&
          event.product &&
          CARD_TYPES.indexOf(event.extensionType) !== -1 &&
          (await ownsProduct(event.product.productId))
        ) {
          log('Regios sospeso', event.extensionType, event.product.productId);
          return { shouldRenderDiscountedPrices: false };
        }
      } catch (error) {
        // In dubbio decide Regios.
      }
      if (typeof previous === 'function') {
        try {
          const outcome = await previous(event);
          if (outcome && typeof outcome.shouldRenderDiscountedPrices === 'boolean') return outcome;
        } catch (error) {
          // Interceptor altrui guasto: comportamento predefinito.
        }
      }
      return { shouldRenderDiscountedPrices: true };
    });
  };

  /* --- Avvio --------------------------------------------------------------- */

  const run = () => {
    const jobs = scan(document.body);
    observer.observe(document.body, { childList: true, subtree: true });
    log('avviato:', jobs.length, 'prezzi di scheda da calcolare');
  };

  const start = async (v0) => {
    if (started || !v0) return;
    started = true;

    const interceptors = v0.interceptors;
    if (
      typeof v0.calculateDiscountedPrices !== 'function' ||
      !interceptors ||
      typeof interceptors.getPriceRenderingInterceptor !== 'function' ||
      typeof interceptors.setPriceRenderingInterceptor !== 'function'
    ) {
      warnOnce('api', 'API Regios senza interceptor: le schede restano come oggi');
      return;
    }
    api = v0;
    try {
      await installInterceptor();
    } catch (error) {
      api = null;
      warnOnce('interceptor', 'interceptor Regios non registrato: le schede restano come oggi', error);
      return;
    }

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run, { once: true });
    else run();
  };

  window.DoppCards = {
    stats() {
      const counts = { calls, cached: cache.size };
      document.querySelectorAll(SELECTOR).forEach((el) => {
        const state = el.getAttribute('data-dopp-state') || 'pending';
        counts[state] = (counts[state] || 0) + 1;
      });
      return counts;
    },
    rescan: () => (api ? scan(document.body).length : 0),
    isRunning: () => api !== null
  };

  const readyApi = () => (window.RegiosDOPP && window.RegiosDOPP.api && window.RegiosDOPP.api.v0) || null;
  if (readyApi()) {
    start(readyApi());
  } else {
    window.addEventListener('regios-dopp:api-initialized', (event) => {
      start((event && event.detail && event.detail.api) || readyApi());
    });
  }
})();
